import React, { useMemo, useEffect, useCallback, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import {
  Row,
  Col,
  Typography,
  Divider,
  Space,
  Tooltip,
  Button,
  notification,
  Checkbox,
} from 'antd'
import {
  ClockCircleOutlined,
  FileOutlined,
  EditOutlined,
  StopOutlined,
  DeleteOutlined,
  HistoryOutlined,
  IssuesCloseOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import moment from 'moment'

import DetailPageLayout from '../../../layouts/DetailPageLayout'
import ProfileCard from '../../../components/ProfileCard'
import MedicalEntityModal from '../../../components/Modals/MedicalEntityModal'
import SpecialAuthorizationFormHistoryModal from '../../../components/Modals/SpecialAuthorizationFormHistoryModal'
import SpecialReleasesFPList from '../../../components/Forms/SpecialReleasesFPList'
import SpecialAuthorizationModal from '../../../components/Modals/SpecialAuthorizationModal'
import UserNotesModal from '../../../components/Modals/UserNotesModal'
import showConfirmDialog from '../../../components/Modals/DeleteConfirm'
import SetRetrieverForm from '../../../components/Forms/SetRetrieverForm'
import SetSpecialReleaseRetrieverModal from '../../../components/Modals/SetSpecialReleaseRetrieverModal'
import CommunicationMethodForm from '../../../components/Forms/CommunicationMethodForm'
import SetCopyCompanyForm from '../../../components/Forms/SetCopyCompanyForm'
import AuthForm from '../../../components/Forms/CreateAuthForm'
import FacilityProfileMRD from '../../../components/FacilityProfileMRD'
import { useUser } from '../../../../providers/UserProvider'
import { downloadFileByUrl } from '../../../../utils/functions'
import {
  formatDateTimeWithDivider,
  convertTextToValue,
  formatWorkingHours,
  formatWeekDays,
  formatDate,
} from '../../../../utils/helpers'

import useDispatchHttp from '../../../../hooks/dispatchHttpHandler'
import {
  fetchFacilityProfileById,
  selectFacilityProfileById,
  updateFacilityProfile,
  setFacilityProfileCommunicationMethod,
  setFacilityProfileCommunicationMethodAutoDetection,
  setFacilityProfileRetriever,
  setFacilityProfileSpecialReleaseRetriever,
  createAuthForm,
  fetchFormById,
  addFacilityToFacilityProfile,
  removeFacilityFromFacilityProfile,
  setMRDFacility,
  getRelatedFacilityProfileFacilities,
  selectRelatedFacilityProfiles,
  fetchFacilityById,
  setFacilityProfileCopyCompany,
  setFacilityProfileCopyCompanyAutoDetection,
  setFacilityProfileMainRetrieverAutoDetection,
  getFacilityProfileESignStat,
  fetchFacilityProfileUserNotesById,
  addFacilityProfileUserNote,
  approveFacilityProfileUserNote,
  updateFacilityProfileUserNote,
  deleteFacilityProfileUserNoteById,
  selectAllUserNotes,
  selectUserNotesCount,
  selectUserNotesStatus,
  selectUserNotesCurrentPage,
  selectUserNotesCurrentPageSize,
} from '../../../../state/modules/facilities'
import {
  DATE_FORMAT,
  DOCTOR,
  DEFAULT_PAGE_SIZE_OPTIONS,
  FACILITY_PROFILE,
} from '../../../../constants'
import { createFacilityProfileSchema } from '../../../../schema/createFacilityProfile'
import { createAuthFormSchema } from '../../../../schema/createAuthForm'
import {
  communicationOptions,
  doctorTableColumns,
  paymentOptions,
  postbillOptions,
} from '../../../../data'
import Table from '../../../components/Tables/SearchTable'
import { fetchDoctorById, selectDoctorById } from '../../../../state/modules/doctors'

const FacilityProfile = () => {
  const { isUserActionAllowed, getUserProfile } = useUser()
  const user = getUserProfile()
  const { id } = useParams()
  const dispatchHttp = useDispatchHttp()
  const dispatch = useDispatch()
  const facilityData = useSelector(state => selectFacilityProfileById(state, id))
  const userNotes = useSelector(selectAllUserNotes)
  const userNotesCount = useSelector(selectUserNotesCount)
  const userNotesLoadingStatus = useSelector(selectUserNotesStatus)
  const userNotesCurrentPage = useSelector(selectUserNotesCurrentPage)
  const userNotesCurrentPageSize = useSelector(selectUserNotesCurrentPageSize)
  const loadingStatus = useSelector(state => state.facilities.status)
  const treatment_facilities = useSelector(selectRelatedFacilityProfiles)
  const doctor = useSelector(state =>
    selectDoctorById(state, facilityData?.sole_proprietor_doctor)
  )
  const {
    count: relatedFacilityProfilesCount,
    page,
    pageSize,
  } = useSelector(state => state.facilities.related_facility_profiles)
  const mrdFacility = facilityData?.mrd_facility
  const [eSignStats, setEsignStats] = useState(null)
  const [mrdFilterValue, setMrdFilterValue] = useState('All')
  const [isAuthHistoryModalVisible, setIsAuthHistoryModalVisible] = useState(false)

  const [isNoteModalVisible, setNoteModalVisible] = useState(false)
  const [isSavingNote, setIsSavingNote] = useState(false)
  const [editingNote, setEditingNote] = useState(null)
  const [userNotesOrdering, setUserNotesOrdering] = useState('-updated_at')
  const [showOnlyUnapproved, setShowOnlyUnapproved] = useState(false)

  const tableFormData = useMemo(() => {
    const filters = {}
    if (showOnlyUnapproved) {
      filters.is_review_required = true
    }
    return {
      facilityProfileId: id,
      data: filters,
    }
  }, [id, showOnlyUnapproved])

  const breadcrumbs = useMemo(
    () => [
      { name: 'Facility Profiles List', path: '/facility-profiles' },
      {
        name: facilityData?.provider_name || '...',
        path: `/facility-profiles/${id}/`,
      },
    ],
    [facilityData?.provider_name, id]
  )

  const initialValues = {
    communication_method: convertTextToValue(
      facilityData?.communication_method,
      communicationOptions
    ),
    payment_type: convertTextToValue(facilityData?.payment_type, paymentOptions),
    postbill: convertTextToValue(facilityData?.postbill, postbillOptions),
    main_retriever: facilityData?.main_retriever?.id,
    last_retriever: facilityData?.last_retriever?.fullname,
    provider_name: facilityData?.provider_name,
    last_case_date: facilityData?.last_case_date,
    case_count: facilityData?.case_count,
    copy_company_name: facilityData?.copy_company_name,
    emr_vendor_name: facilityData?.emr_vendor_name,
    emr_data_source: facilityData?.emr_data_source,
    working_hours_start: facilityData?.working_hours_start || '09:00:00',
    working_hours_end: facilityData?.working_hours_end || '17:00:00',
    turn_around_min: facilityData?.turn_around_min,
    turn_around_max: facilityData?.turn_around_max,
    turn_around_avg: facilityData?.turn_around_avg,
    contact_name: facilityData?.contact_name,
    min_fee: facilityData?.min_fee,
    max_fee: facilityData?.max_fee,
    avg_facility_records_fee: facilityData?.avg_facility_records_fee,
    avg_fee: facilityData?.avg_fee,
    total_paid: facilityData?.total_paid,
    days_open: facilityData?.days_open,
    notes: facilityData?.notes,
    email: facilityData?.email,
  }

  const handleFacilityProfileUpdate = useCallback(
    async (facilityId, data) => {
      await dispatchHttp(
        updateFacilityProfile({
          facilityId,
          data: {
            days_open: data.days_open || [],
            email: data.email || '',
            contact_name: data.contact_name || '',
            emr_data_source: data.emr_data_source || '',
            emr_vendor_name: data.emr_vendor_name || '',
            notes: data.notes || '',
            working_hours_end: data.working_hours_end || null,
            working_hours_start: data.working_hours_start || null,
          },
        }),
        'Facility Profile has been successfully updated'
      )
      await dispatchHttp(fetchFacilityProfileById(id))
    },
    [dispatchHttp, id]
  )

  const handleSetMRDFacility = useCallback(
    async facility_id => {
      await dispatchHttp(
        setMRDFacility({
          id,
          data: { facility_id },
        }),
        'Facility Profile  has been successfully updated'
      )
      await dispatchHttp(fetchFacilityProfileById(id))

      const params = { data: id, page, pageSize }
      if (mrdFilterValue === 'Yes') {
        params.special_release = true
      } else if (mrdFilterValue === 'No') {
        params.special_release = false
      }

      await dispatchHttp(getRelatedFacilityProfileFacilities(params))
    },
    [dispatchHttp, id, page, pageSize, mrdFilterValue]
  )

  const handleSetRetriever = useCallback(
    async (facilityId, data, isAutoDetectionEnabled) => {
      if (
        data.main_retriever &&
        isUserActionAllowed('update_retrievers_facilityprofile')
      ) {
        await dispatchHttp(
          setFacilityProfileRetriever({
            facilityId,
            data,
          })
        )
      }

      if (isUserActionAllowed('update_main_retriever_auto_detection')) {
        await dispatchHttp(
          setFacilityProfileMainRetrieverAutoDetection({
            facilityId,
            data: {
              auto_detect_main_retriever: isAutoDetectionEnabled,
            },
          }),
          'Facility Profile has been successfully updated'
        )
      }

      await dispatchHttp(fetchFacilityProfileById(id))
    },
    // eslint-disable-next-line
    [dispatchHttp, id]
  )

  const handleSetSpecialReleaseRetriever = useCallback(
    async ({ id: retrieverId }) => {
      await dispatchHttp(
        setFacilityProfileSpecialReleaseRetriever({
          facilityId: id,
          data: { special_release_retriever: retrieverId },
        }),
        'Facility Profile has been successfully updated'
      )
      await dispatchHttp(fetchFacilityProfileById(id))
    },
    [dispatchHttp, id]
  )

  const handleChangeCommunicationMethod = useCallback(
    async (facilityId, communicationMethod, isAutoDetectionEnabled) => {
      const promises = [
        dispatchHttp(
          setFacilityProfileCommunicationMethodAutoDetection({
            facilityId,
            data: {
              auto_detect_communication_method: isAutoDetectionEnabled,
            },
          })
        ),
      ]

      if (!isAutoDetectionEnabled) {
        promises.push(
          dispatchHttp(
            setFacilityProfileCommunicationMethod({
              facilityId,
              data: {
                communication_method: communicationMethod,
              },
            })
          )
        )
      }

      await Promise.all(promises)
      notification.success({
        message: 'Facility Profile has been successfully updated',
      })

      await dispatchHttp(fetchFacilityProfileById(id))
    },
    // eslint-disable-next-line
    [dispatchHttp, id]
  )

  const handleSetCopyCompany = useCallback(
    async (facilityId, data, isAutoDetectionEnabled) => {
      if (
        data.selected_id &&
        isUserActionAllowed('update_copy_company_facilityprofile')
      ) {
        const siteId = data[`site_id_${data.selected_id}`]
        const copyCompanyId = data.selected_id
        await dispatchHttp(
          setFacilityProfileCopyCompany({
            facilityId,
            data: {
              copy_company_id: copyCompanyId,
              site_id: siteId || null,
            },
          })
        )
      }

      if (isUserActionAllowed('update_copy_company_auto_detection')) {
        await dispatchHttp(
          setFacilityProfileCopyCompanyAutoDetection({
            facilityId,
            data: {
              auto_detect_copy_company: isAutoDetectionEnabled,
            },
          }),
          'Facility Profile has been successfully updated'
        )
      }

      await dispatchHttp(fetchFacilityProfileById(id))
    },
    // eslint-disable-next-line
    [dispatchHttp, id]
  )

  const handleFormCreate = useCallback(
    async (_, data) => {
      await dispatchHttp(
        createAuthForm({ ...data, facility_profile_id: id }),
        'Authorization Form has been successfully created'
      )
      await dispatchHttp(fetchFacilityProfileById(id))
    },
    [dispatchHttp, id]
  )

  const unlinkFacilityFromFacilityProfile = async data => {
    await dispatchHttp(
      removeFacilityFromFacilityProfile({
        id,
        data: { facility_id: data.id },
      }),
      'Facility has been successfully removed from profile'
    )
    await dispatchHttp(fetchFacilityProfileById(id))
    await dispatchHttp(getRelatedFacilityProfileFacilities({ data: id }))
  }

  const relateFacilityProfile = async data => {
    await dispatchHttp(
      addFacilityToFacilityProfile({
        id,
        data: {
          facility_id: data.id,
        },
      }),
      'Facility has been successfully added to the facility profile'
    )

    await dispatchHttp(fetchFacilityProfileById(id))
    await dispatchHttp(getRelatedFacilityProfileFacilities({ data: id }))
  }

  const handleNotesFetch = useCallback(
    payloadFromTable => {
      const correctFilters = {}
      if (showOnlyUnapproved) {
        correctFilters.is_review_required = true
      }

      const finalPayload = {
        ...payloadFromTable,
        facilityProfileId: id,
        data: correctFilters,
      }

      return fetchFacilityProfileUserNotesById(finalPayload)
    },
    [id, showOnlyUnapproved]
  )

  const handleAddUserNote = () => {
    setEditingNote(null)
    setNoteModalVisible(true)
  }

  const handleEditUserNoteClick = useCallback(noteToEdit => {
    setEditingNote(noteToEdit)
    setNoteModalVisible(true)
  }, [])

  const handleCancelAddNote = () => {
    setNoteModalVisible(false)
    setEditingNote(null)
  }

  const handleApproveUserNoteClick = useCallback(
    async noteToApprove => {
      try {
        await dispatchHttp(
          approveFacilityProfileUserNote({
            fpId: id,
            id: noteToApprove.id,
            note: noteToApprove.note,
          }),
          'Note has been successfully approved'
        )

        const isLastItemOnPage = userNotes.length === 1 && userNotesCurrentPage > 1
        const nextPage = isLastItemOnPage
          ? userNotesCurrentPage - 1
          : userNotesCurrentPage

        dispatch(
          fetchFacilityProfileUserNotesById({
            page: nextPage,
            pageSize: userNotesCurrentPageSize,
            ...tableFormData,
            ordering: userNotesOrdering,
          })
        )
      } catch (error) {
        notification.error({
          message: 'Approve Failed',
          description: 'Could not approve the note.',
        })
      }
    },
    [
      dispatch,
      dispatchHttp,
      id,
      tableFormData,
      userNotesCurrentPage,
      userNotesCurrentPageSize,
      userNotesOrdering,
      userNotes.length,
    ]
  )

  const handleSaveNewNote = useCallback(
    async (values, { resetForm }) => {
      const trimmedContent = values.note.trim()
      setIsSavingNote(true)
      try {
        if (editingNote) {
          await dispatchHttp(
            updateFacilityProfileUserNote({
              fpId: id,
              id: editingNote.id,
              note: trimmedContent,
            }),
            'Note has been successfully updated'
          )
          dispatch(
            fetchFacilityProfileUserNotesById({
              page: userNotesCurrentPage,
              pageSize: userNotesCurrentPageSize,
              ...tableFormData,
              ordering: userNotesOrdering,
            })
          )
        } else {
          await dispatchHttp(
            addFacilityProfileUserNote({ id, data: { note: trimmedContent } }),
            'Note has been successfully added'
          )
          if (!showOnlyUnapproved) {
            setUserNotesOrdering('-updated_at')
          }
          dispatch(
            fetchFacilityProfileUserNotesById({
              page: showOnlyUnapproved ? userNotesCurrentPage : 1,
              pageSize: userNotesCurrentPageSize,
              ...tableFormData,
              ordering: showOnlyUnapproved ? userNotesOrdering : '-updated_at',
            })
          )
        }
        resetForm()
        setNoteModalVisible(false)
        setEditingNote(null)
      } catch (error) {
        notification.error({
          message: 'Save Failed',
          description: 'Could not save the note.',
        })
      } finally {
        setIsSavingNote(false)
      }
    },
    [
      dispatch,
      userNotesCurrentPage,
      userNotesCurrentPageSize,
      tableFormData,
      editingNote,
      dispatchHttp,
      id,
      userNotesOrdering,
      showOnlyUnapproved,
    ]
  )

  const handleRemoveUserNote = useCallback(
    async data => {
      await dispatchHttp(
        deleteFacilityProfileUserNoteById({ id, noteId: data.id }),
        'Note has been successfully removed'
      )

      const isLastItemOnPage = userNotes.length === 1 && userNotesCurrentPage > 1
      const nextPage = isLastItemOnPage ? userNotesCurrentPage - 1 : userNotesCurrentPage

      dispatch(
        fetchFacilityProfileUserNotesById({
          page: nextPage,
          pageSize: userNotesCurrentPageSize,
          ...tableFormData,
          ordering: userNotesOrdering,
        })
      )
    },
    [
      dispatch,
      dispatchHttp,
      id,
      tableFormData,
      userNotesCurrentPageSize,
      userNotesOrdering,
      userNotes.length,
      userNotesCurrentPage,
    ]
  )

  useEffect(() => {
    dispatchHttp(fetchFacilityProfileById(id))
  }, [dispatchHttp, id])

  useEffect(() => {
    if (
      facilityData &&
      !facilityData?.sole_proprietor_doctor &&
      isUserActionAllowed('view_facility')
    ) {
      dispatchHttp(getRelatedFacilityProfileFacilities({ data: id }))
    }
    // eslint-disable-next-line
  }, [dispatchHttp, id, facilityData?.id])

  useEffect(() => {
    if (id) {
      dispatch(
        fetchFacilityProfileUserNotesById({
          page: 1,
          pageSize: parseInt(DEFAULT_PAGE_SIZE_OPTIONS[0], 10),
          ...tableFormData,
          ordering: userNotesOrdering,
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch, tableFormData, userNotesOrdering])

  useEffect(() => {
    if (facilityData?.sole_proprietor_doctor && isUserActionAllowed('view_doctor')) {
      dispatchHttp(fetchDoctorById(facilityData.sole_proprietor_doctor))
    }
    // eslint-disable-next-line
  }, [dispatchHttp, facilityData?.sole_proprietor_doctor])

  useEffect(() => {
    if (facilityData?.mrd_facility_id && isUserActionAllowed('view_facility')) {
      dispatchHttp(fetchFacilityById(facilityData.mrd_facility_id))
    }
    // eslint-disable-next-line
  }, [dispatchHttp, facilityData?.id])

  const getEsignStats = async facilityId => {
    const resp = await dispatchHttp(
      getFacilityProfileESignStat({
        facility_profile: facilityId,
      })
    )
    setEsignStats(resp)
  }

  useEffect(() => {
    if (facilityData?.id) {
      getEsignStats(facilityData?.id)
    }
    // eslint-disable-next-line
  }, [dispatchHttp, facilityData?.id])

  const currentForm = useMemo(
    () => facilityData?.special_release_forms?.find(form => form?.is_current),
    [facilityData?.special_release_forms]
  )

  const downloadForm = useCallback(async () => {
    if (!currentForm) return
    const data = await dispatchHttp(fetchFormById({ formId: currentForm.id, id }))
    downloadFileByUrl(data?.changes?.link)
  }, [dispatchHttp, currentForm, id])

  const isShowDownloadForm =
    !!currentForm && isUserActionAllowed('download_authorizationform')

  const isShowCreateForm =
    !currentForm &&
    isUserActionAllowed('add_authorizationform') &&
    isUserActionAllowed('view_authorizationform')

  const isShowDivider =
    isShowDownloadForm && isUserActionAllowed('view_authorizationform')

  const isShowHistory = !!currentForm && isUserActionAllowed('view_authorizationform')

  const isShowAuthFormSection =
    isShowDownloadForm ||
    isShowCreateForm ||
    isUserActionAllowed('view_authorizationform')

  const mainRetriever = facilityData?.main_retriever_definition?.contname ? (
    <Tooltip title="Click to set new Main Retriever">
      {facilityData?.main_retriever_definition?.fullname ? (
        <span>
          {facilityData?.main_retriever_definition?.fullname}
          <Divider type="vertical" className="profile-card__divider" />
        </span>
      ) : null}
      {facilityData?.main_retriever_definition?.contname}
    </Tooltip>
  ) : (
    facilityData?.main_retriever_definition?.fullname
  )

  const specialReleaseRetriever = facilityData?.special_release_retriever?.contname ? (
    <Tooltip title="Click to set new Special Release Retriever">
      {facilityData?.special_release_retriever?.fullname ? (
        <span>
          {facilityData?.special_release_retriever?.fullname}
          <Divider type="vertical" className="profile-card__divider" />
        </span>
      ) : null}
      {facilityData?.special_release_retriever?.contname}
    </Tooltip>
  ) : (
    facilityData?.special_release_retriever?.fullname
  )

  const lastRetriever =
    facilityData?.last_retriever?.fullname || facilityData?.last_retriever?.contname ? (
      <span>
        {facilityData?.last_retriever?.fullname ? (
          <span>{facilityData?.last_retriever?.fullname}</span>
        ) : null}
        {facilityData?.last_retriever?.contname}
      </span>
    ) : (
      '-'
    )

  const primaryCopyCompany =
    facilityData?.copy_companies.find(item => item.is_primary) ||
    facilityData?.copy_companies[0]

  const facilityProfileUserNotesColumns = [
    {
      title: 'Author',
      key: 'author_id',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      width: 200,
      render: (text, properties, noteToEdit) => (
        <span>
          {noteToEdit?.author?.firstname} {noteToEdit?.author?.lastname}
        </span>
      ),
    },
    {
      title: 'Creation Date',
      key: 'created_at',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      width: 150,
      date: true,
      render: text => (
        <Space>
          <span>{formatDateTimeWithDivider(text)}</span>
        </Space>
      ),
    },
    {
      title: 'Updated Date',
      key: 'updated_at',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      width: 150,
      date: true,
      render: text => (
        <Space>
          <span>{formatDateTimeWithDivider(text)}</span>
        </Space>
      ),
    },
    {
      title: 'Note',
      key: 'note',
      render: text => <span>{text}</span>,
    },
    ...(isUserActionAllowed('view_version')
      ? [
          {
            title: 'Activity Log',
            key: 'id',
            align: 'center',
            width: 106,
            // eslint-disable-next-line react/display-name, no-shadow
            render: id => (
              <div>
                <Link
                  to={`/user-activity-log/facility_profile_note/${id}?edited=all&entity_filter=all`}
                >
                  <Space>
                    <HistoryOutlined />
                    Check
                  </Space>
                </Link>
              </div>
            ),
          },
        ]
      : []),
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, properties, note) => {
        const canApproveOrEdit = user.is_superuser || note.author.id === user.id
        const canDelete = user.is_superuser || note.author.id === user.id

        const shouldShowUnapprovedLabel = note.is_review_required && !canApproveOrEdit

        return (
          <Space>
            {shouldShowUnapprovedLabel && (
              <span className="user-notes-table-actions-unapproved-label">
                <InfoCircleOutlined style={{ marginRight: '4px' }} />
                Unapproved
              </span>
            )}

            {canApproveOrEdit && note.is_review_required && (
              <Tooltip title="Approve">
                <Button
                  type="text"
                  icon={
                    <IssuesCloseOutlined style={{ color: '#FF9863', fontSize: 18 }} />
                  }
                  onClick={() => handleApproveUserNoteClick(note)}
                  aria-label="Approve Note"
                />
              </Tooltip>
            )}
            {canApproveOrEdit && !note.is_review_required && (
              <Tooltip title="Edit">
                <Button
                  type="text"
                  icon={<EditOutlined style={{ color: '#BFBFBF', fontSize: 18 }} />}
                  onClick={() => handleEditUserNoteClick(note)}
                  aria-label="Edit Note"
                />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Remove">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined style={{ color: '#BFBFBF', fontSize: 18 }} />}
                  aria-label="Delete Note"
                  onClick={() =>
                    showConfirmDialog({
                      title: 'Are you sure you want to remove this note?',
                      data: { id: note.id },
                      handleConfirm: handleRemoveUserNote,
                    })
                  }
                />
              </Tooltip>
            )}
          </Space>
        )
      },
    },
  ]

  return (
    <DetailPageLayout breadcrumbs={breadcrumbs}>
      <div className="facility-profile-content-container">
        <Row>
          {facilityData?.sole_proprietor_doctor && isUserActionAllowed('view_doctor') && (
            <Col span={24}>
              <ProfileCard
                title={<Space size={16}>Provider</Space>}
                style={{
                  headerColor: '#E6F7FF',
                  bodyColor: '#003A8C',
                  padding: 20,
                }}
              >
                <Table
                  columns={doctorTableColumns.filter(
                    item =>
                      item.key !== 'facility_profile' && item.key !== 'related_facilities'
                  )}
                  totalData={[doctor]}
                  entity={DOCTOR}
                />
              </ProfileCard>
            </Col>
          )}

          <FacilityProfileMRD
            handleSetMRDFacility={handleSetMRDFacility}
            unlinkFacilityFromFacilityProfile={unlinkFacilityFromFacilityProfile}
            relateFacilityProfile={relateFacilityProfile}
            treatment_facilities={treatment_facilities}
            mrd_facility={isUserActionAllowed('view_facility') && mrdFacility}
            relatedFacilityProfilesCount={relatedFacilityProfilesCount}
            id={id}
            isUserActionAllowed={isUserActionAllowed}
            filterValue={mrdFilterValue}
            onFilterChange={setMrdFilterValue}
            facilityData={facilityData}
            initialValues={initialValues}
            handleFacilityProfileUpdate={handleFacilityProfileUpdate}
            createFacilityProfileSchema={createFacilityProfileSchema}
          />

          <Col span={24}>
            <ProfileCard
              title="Overview"
              color="yellow"
              loading={loadingStatus}
              content={[
                {
                  label: 'Communication Method',
                  value: (
                    <>
                      <MedicalEntityModal
                        title="Change Communication Method"
                        className="communication-method-modal"
                        width={400}
                        trigger={
                          isUserActionAllowed('change_facilityprofile') ? (
                            <a href="/">
                              {!facilityData?.auto_detect_communication_method && (
                                <StopOutlined style={{ marginRight: '4px' }} />
                              )}
                              {facilityData?.communication_method}
                            </a>
                          ) : (
                            <>
                              {!facilityData?.auto_detect_communication_method && (
                                <StopOutlined style={{ marginRight: '4px' }} />
                              )}
                              {facilityData?.communication_method}
                            </>
                          )
                        }
                        form={
                          <CommunicationMethodForm
                            initialValues={{
                              ...initialValues,
                              id: facilityData?.id,
                            }}
                            isAutoDetection={
                              facilityData?.auto_detect_communication_method
                            }
                            onSubmit={handleChangeCommunicationMethod}
                            entityId={id}
                          />
                        }
                      />
                    </>
                  ),
                },
                {
                  label: 'Payment Method',
                  value: facilityData?.payment_type,
                },
                {
                  label: 'Last Case Date',
                  value: moment(facilityData?.last_case_date).isValid()
                    ? formatDate(facilityData?.last_case_date, DATE_FORMAT.month_day_year)
                    : '-',
                },
                {
                  label: 'Case Count',
                  value: facilityData?.case_count,
                },
                {
                  label: 'Turn Around Time',
                  value: (
                    <Space size={24}>
                      <span>
                        <Typography.Text type="secondary">Min </Typography.Text>
                        {facilityData?.turn_around_min} d
                      </span>
                      <span>
                        <Typography.Text type="secondary">Max </Typography.Text>
                        {facilityData?.turn_around_max} d
                      </span>
                      <span>
                        <Typography.Text type="secondary">Avg </Typography.Text>
                        {facilityData?.turn_around_avg} d
                      </span>
                    </Space>
                  ),
                },
              ].filter(Boolean)}
            />
            <ProfileCard
              title="Special Release Priority"
              color="orange"
              loading={loadingStatus}
              content={[
                isUserActionAllowed('view_retriever') && {
                  label: 'Special Release Retriever',
                  value: isUserActionAllowed('change_facilityprofile') ? (
                    <div style={{ minWidth: 206 }}>
                      <SetSpecialReleaseRetrieverModal
                        title="Set Special Release Retriever"
                        assignedSpecialReleaseRetrieverId={
                          facilityData?.special_release_retriever?.cms_id ?? null
                        }
                        trigger={
                          <a href="/">
                            {specialReleaseRetriever || 'Set Special Release Retriever'}
                          </a>
                        }
                        onSubmit={handleSetSpecialReleaseRetriever}
                      />
                    </div>
                  ) : (
                    <div style={{ minWidth: 206 }}>{specialReleaseRetriever}</div>
                  ),
                },
                {
                  label: 'Verification',
                  value: facilityData?.e_signature,
                },
                {
                  label: 'Accepted',
                  value: eSignStats?.e_signature_accepted_count,
                },
                {
                  label: 'Accepted With Conditions',
                  value: eSignStats?.e_signature_accepted_with_conditions_count,
                },
                {
                  label: 'Not Approved',
                  value: eSignStats?.e_signature_not_approved_count,
                },
                {
                  label: 'Special Authorization',
                  value: (
                    <div style={{ minWidth: 164 }}>
                      <SpecialAuthorizationModal
                        className="set-retriever-modal"
                        width={1200}
                        facilityId={facilityData?.id}
                        trigger={<a href="/">By Insurance Company</a>}
                      />
                    </div>
                  ),
                },
                {
                  label: 'Special Release Form Requirement',
                  value: (
                    <div style={{ minWidth: 232 }}>{facilityData?.special_auth}</div>
                  ),
                },
                {
                  label: 'Special Release Form',
                  authForm: true,
                  value: (
                    <div style={{ minWidth: 184 }}>
                      {isShowDownloadForm && (
                        <Typography.Link onClick={downloadForm}>
                          <Space size={6}>
                            <FileOutlined />
                            Download
                          </Space>
                        </Typography.Link>
                      )}
                      {isShowCreateForm && (
                        <MedicalEntityModal
                          title="Attach Special Release Form"
                          className="set-retriever-modal"
                          width={580}
                          trigger={
                            <Typography.Link>
                              <Space size={6}>
                                <FileOutlined />
                                Upload
                              </Space>
                            </Typography.Link>
                          }
                          form={
                            <AuthForm
                              validationSchema={createAuthFormSchema}
                              onSubmit={handleFormCreate}
                              initialValues={{
                                attachment: null,
                              }}
                            />
                          }
                        />
                      )}
                      {isShowDivider && (
                        <Divider type="vertical" className="profile-card__divider" />
                      )}
                      {isShowHistory && (
                        <Typography.Link
                          onClick={() => setIsAuthHistoryModalVisible(true)}
                        >
                          <Space size={6}>
                            <ClockCircleOutlined /> History
                          </Space>
                        </Typography.Link>
                      )}
                    </div>
                  ),
                },
              ].filter(
                item =>
                  item && (!item.authForm || (item.authForm && isShowAuthFormSection))
              )}
            />
            <ProfileCard
              title="Vendors"
              color="green"
              loading={loadingStatus}
              content={[
                {
                  label: 'Site ID | Copy Company',
                  value: isUserActionAllowed('view_copycompany') ? (
                    <>
                      {' '}
                      {!facilityData?.auto_detect_copy_company && (
                        <StopOutlined style={{ marginRight: '4px' }} />
                      )}
                      {primaryCopyCompany?.site_id ? (
                        <>{primaryCopyCompany.site_id}</>
                      ) : (
                        '-'
                      )}
                      <Divider type="vertical" className="profile-card__divider" />
                      <MedicalEntityModal
                        title="Set Primary Copy Company"
                        className="set-retriever-modal"
                        width={1200}
                        trigger={
                          <a href="/">
                            {facilityData?.copy_companies_definition ||
                              'Set Copy Company'}
                          </a>
                        }
                        form={
                          <SetCopyCompanyForm
                            initialValues={{
                              copy_company_id: primaryCopyCompany?.id,
                              id: facilityData?.id,
                            }}
                            isAutoDetection={facilityData?.auto_detect_copy_company}
                            fetchFacilityProfileById={fetchFacilityProfileById}
                            onSubmit={handleSetCopyCompany}
                            entityId={id}
                            isUserActionAllowed={isUserActionAllowed}
                          />
                        }
                      />
                    </>
                  ) : (
                    <>
                      {!facilityData?.auto_detect_copy_company && (
                        <StopOutlined style={{ marginRight: '4px' }} />
                      )}
                      {primaryCopyCompany?.site_id ? (
                        <>{primaryCopyCompany.site_id}</>
                      ) : (
                        '-'
                      )}
                      <Divider type="vertical" className="profile-card__divider" />
                      {primaryCopyCompany?.name ?? 'Set Copy Company'}
                    </>
                  ),
                },
                isUserActionAllowed('view_retriever') && {
                  label: 'Main Retriever',
                  value: (
                    <>
                      <MedicalEntityModal
                        title="Set Main Retriever"
                        className="set-retriever-modal"
                        width={1200}
                        trigger={
                          <a href="/">
                            {!facilityData?.auto_detect_main_retriever && (
                              <StopOutlined style={{ marginRight: '4px' }} />
                            )}
                            {mainRetriever}
                          </a>
                        }
                        form={
                          <SetRetrieverForm
                            initialValues={{
                              ...initialValues,
                              id: facilityData?.id,
                            }}
                            isAutoDetection={facilityData?.auto_detect_main_retriever}
                            onSubmit={handleSetRetriever}
                            entityId={id}
                            isUserActionAllowed={isUserActionAllowed}
                          />
                        }
                      />
                    </>
                  ),
                },
                isUserActionAllowed('view_retriever') && {
                  label: 'Past Retrievers',
                  value: lastRetriever,
                },
              ].filter(Boolean)}
            />
            <ProfileCard
              title="Schedule"
              color="blue"
              loading={loadingStatus}
              content={[
                {
                  label: 'Days Open',
                  value: formatWeekDays(facilityData?.days_open),
                },
                {
                  label: 'Hours Open',
                  value: formatWorkingHours(
                    facilityData?.working_hours_start,
                    facilityData?.working_hours_end
                  ),
                },
                {
                  label: 'Contact Name of Medical Department',
                  value: facilityData?.contact_name,
                },
                {
                  label: 'Email',
                  value: facilityData?.email,
                },
              ]}
            />
            <ProfileCard
              title="Finance"
              color="magenta"
              loading={loadingStatus}
              content={[
                {
                  label: 'Min Fee',
                  value: `$${facilityData?.min_fee}`,
                },
                {
                  label: 'Max Fee',
                  value: `$${facilityData?.max_fee}`,
                },
                {
                  label: 'Average Facility Records Fee Per Page',
                  value: `$${facilityData?.avg_facility_records_fee}`,
                },
                {
                  label: 'Avg Fee',
                  value: `$${facilityData?.avg_fee}`,
                },
                {
                  label: 'Postbill',
                  value: facilityData?.postbill,
                },
                {
                  label: 'Total Paid',
                  value: `$${facilityData?.total_paid}`,
                },
              ]}
            />
          </Col>
        </Row>
        {facilityData?.notes ? (
          <Row>
            <Col span={24}>
              <ProfileCard
                title={<Space size={16}>General Note</Space>}
                style={{
                  headerColor: '#e3eddd',
                  bodyColor: '#003A8C',
                }}
              >
                <p
                  style={{
                    width: '100%',
                    whiteSpace: 'pre-line',
                    backgroundColor: '#fff',
                    padding: '4px 16px',
                    margin: 0,
                  }}
                >
                  {facilityData?.notes}
                </p>
              </ProfileCard>
            </Col>
          </Row>
        ) : (
          <></>
        )}
        {userNotes ? (
          <Row
            style={{
              marginBottom: '12px',
            }}
          >
            <Col span={24}>
              <div className="user-notes-table">
                <div className="user-notes-table-title">
                  <span>User Notes</span>
                  <div className="user-notes-table-actions">
                    <Checkbox
                      className="user-notes-table-title-checkbox"
                      checked={showOnlyUnapproved}
                      onChange={e => setShowOnlyUnapproved(e.target.checked)}
                    >
                      Show Only Unapproved
                    </Checkbox>
                    <Button
                      className="user-notes-table-title-button"
                      type="primary"
                      ghost
                      onClick={handleAddUserNote}
                      aria-label="Add Note"
                    >
                      Add New Note
                    </Button>
                  </div>
                </div>
                {userNotes.length ? (
                  <Table
                    columns={facilityProfileUserNotesColumns}
                    totalData={userNotes}
                    totalCount={userNotesCount}
                    onLoadData={handleNotesFetch}
                    loadingStatus={userNotesLoadingStatus}
                    entity={FACILITY_PROFILE}
                    formData={tableFormData}
                    defaultSortField="updated_at"
                    defaultSortOrder="descend"
                    onSortChange={setUserNotesOrdering}
                    controlledOrdering={userNotesOrdering}
                    currentPage={userNotesCurrentPage}
                  />
                ) : (
                  <p className="user-notes-table-empty-content">No results</p>
                )}
              </div>
            </Col>
          </Row>
        ) : null}
      </div>

      <UserNotesModal
        visible={isNoteModalVisible}
        onCancel={handleCancelAddNote}
        onSubmit={handleSaveNewNote}
        isSaving={isSavingNote}
        editingNote={editingNote}
      />

      {isShowAuthFormSection && isShowHistory && (
        <SpecialAuthorizationFormHistoryModal
          title="Special Release Forms History"
          className="special-release-form-modal"
          width={800}
          visible={isAuthHistoryModalVisible}
          onCancel={() => setIsAuthHistoryModalVisible(false)}
        >
          <SpecialReleasesFPList
            facilityProfileId={id}
            onSubmit={handleFormCreate}
            canDelete={isUserActionAllowed('delete_authorizationform')}
            onClose={() => setIsAuthHistoryModalVisible(false)}
          />
        </SpecialAuthorizationFormHistoryModal>
      )}
    </DetailPageLayout>
  )
}

export default FacilityProfile
